import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, NavigationExtras, Router, RouterLink } from '@angular/router';
import { UserService } from '@lib/user';
import { BaseSiteLayoutModule, DayjsModule, DialogOptions, HttpErrorAlertModule, SubscriptionHelper } from '@lib/util';
import { CopyToClipboardModule, PlatformModule, View } from '@lib/core';
import { firstValueFrom } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { DeliveryReceiptDataService } from '../delivery-receipt-data.service';
import {
    ProcessChainDataService,
    ProcessChainDocumentType,
    ProcessChainRelatedDocument,
    ProcessChainRelatedDocumentComponent,
} from '@lib/process-chain';
import { DeliveryReceiptFileService } from '../delivery-receipt-file.service';
import { DeliveryReceiptPossibleActionType, IDeliveryReceiptViewResponse } from '../types/IDeliveryReceipt';
import { DeliveryReceiptItemSearchResult } from '../types/DeliveryReceiptItemSearchResult';
import { DeliveryReceiptDetailService } from './delivery-receipt-detail.service';
import { DeliveryReceiptItem } from '../types/IDeliveryReceiptItem';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { BC, BreadcrumbService } from '@lib/shared/breadcrumbs';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import {
    DocumentItemActionComponent,
    DocumentItemActionIconComponent,
    DocumentItemAttributeComponent,
    DocumentItemComponent,
    DocumentItemDescriptionComponent,
    DocumentItemLabelComponent,
    DocumentItemNameComponent,
    DocumentItemNoteComponent,
    DocumentItemPositionIndicatorComponent,
    DocumentItemPriceComponent,
    DocumentItemValueComponent,
    DocumentLayoutComponent,
    DocumentSidemenuAttachmentComponent,
    DocumentSidemenuCardComponent,
    DocumentSidemenuSectionActionComponent,
    DocumentSidemenuSectionComponent,
    DocumentSidemenuSectionEmptyMessageComponent,
    DocumentSidemenuSectionHeaderComponent,
} from '@lib/document';
import { IimCardModule, IimSpinnerModule } from '@iim-angular/components';
import { UnitModule } from '@lib/unit';
import { WarehouseAclAction, WarehouseActiveModule, WarehouseDataService } from '@lib/warehouse';
import { CompanyAddressModule } from '@lib/address';
import { CompanyCodeDataService, ICompanyCode } from '@lib/company-code';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AclModule, AclResource } from '@lib/acl';
import { BarcodeScanner } from '@awesome-cordova-plugins/barcode-scanner/ngx';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SupplierDisplayNamePipe } from '@lib/supplier';
import {
    BulkInsertIntoWarehouseDialogComponent,
    BulkInsertIntoWarehouseDialogData,
    BulkInsertIntoWarehouseDialogResult,
} from './warehouse-insert/bulk-insert-into-warehouse-dialog.component';

export class DeliveryReceiptDetailDialogData {
    constructor(public readonly receiptId: number) {}
}

@Component({
    selector: 'app-delivery-receipt-detail',
    templateUrl: './delivery-receipt-detail.component.html',
    styleUrls: ['./delivery-receipt-detail.component.scss'],
    imports: [
        CommonModule,
        TranslateModule,
        DocumentItemComponent,
        IimSpinnerModule,
        HttpErrorAlertModule,
        DayjsModule,
        IimCardModule,
        UnitModule,
        DocumentItemValueComponent,
        DocumentItemLabelComponent,
        DocumentItemPriceComponent,
        DocumentItemNoteComponent,
        DocumentItemAttributeComponent,
        RouterLink,
        CopyToClipboardModule,
        DocumentItemPositionIndicatorComponent,
        DocumentItemDescriptionComponent,
        DocumentItemNameComponent,
        DocumentItemActionComponent,
        WarehouseActiveModule,
        CompanyAddressModule,
        BaseSiteLayoutModule,
        DocumentLayoutComponent,
        DocumentSidemenuSectionComponent,
        DocumentSidemenuSectionHeaderComponent,
        DocumentSidemenuSectionEmptyMessageComponent,
        DocumentSidemenuAttachmentComponent,
        DocumentSidemenuCardComponent,
        ReactiveFormsModule,
        DocumentSidemenuSectionActionComponent,
        AclModule,
        DocumentItemActionIconComponent,
        PlatformModule,
        MatTooltipModule,
        ProcessChainRelatedDocumentComponent,
        SupplierDisplayNamePipe,
    ],
})
export class DeliveryReceiptDetailComponent extends SubscriptionHelper implements OnInit {
    private readonly route = inject(ActivatedRoute);
    readonly deliveryReceiptDataService = inject(DeliveryReceiptDataService);
    private readonly deliveryReceiptDetailService = inject(DeliveryReceiptDetailService);
    private readonly userService = inject(UserService);
    private readonly companyCodeDataService = inject(CompanyCodeDataService);
    private readonly deliveryReceiptFileService = inject(DeliveryReceiptFileService);
    private readonly breadcrumbService = inject(BreadcrumbService);
    private readonly dialog = inject(MatDialog);
    private readonly warehouseDataService = inject(WarehouseDataService);
    private readonly _dialogRef = inject<MatDialogRef<DeliveryReceiptDetailComponent>>(MatDialogRef, {
        optional: true,
    })!;
    private readonly _dialogData = inject<DeliveryReceiptDetailDialogData>(MAT_DIALOG_DATA, { optional: true })!;

    private readonly _router = inject(Router);
    private readonly _destroyRef = inject(DestroyRef);
    private readonly _barcodeScanner = inject(BarcodeScanner);
    private readonly _processChainDataService = inject(ProcessChainDataService);

    public readonly COMMENT_MAX_LENGTH = 1000;
    protected readonly AclResource = AclResource;
    deliveryReceiptPossibleActionType = DeliveryReceiptPossibleActionType;

    public receipt?: IDeliveryReceiptViewResponse;
    public errorStatusCode?: number;
    public receiptsGroupedByOrder?: Map<string, DeliveryReceiptItem[]>;
    public groupedRelatedItems?: Map<string, Map<string, DeliveryReceiptItemSearchResult[]>>;
    public view?: View;
    public companyCodes: ICompanyCode[] = [];
    public selectedCompanyCode?: ICompanyCode;
    public hasMultipleCompanyCodes = false;
    protected readonly View = View;
    protected readonly WarehouseAclAction = WarehouseAclAction;

    public commentCtrl = new FormControl<string>('', {
        nonNullable: true,
        validators: [Validators.maxLength(this.COMMENT_MAX_LENGTH)],
    });

    private readonly fetchedLinkedDocuments = signal<ProcessChainRelatedDocument[]>([]);
    protected readonly linkedProcessDocuments = computed(() =>
        this.fetchedLinkedDocuments().map((document) => ({
            ...document,
            targetRoute: this.resolveLinkedDocumentRoute(document),
        })),
    );

    public get isDialog(): boolean {
        return !!this._dialogRef;
    }

    public get receiptId(): number {
        if (this.isDialog) {
            return this._dialogData.receiptId;
        }

        return +this.route.snapshot.params['receiptId'];
    }

    public get unstockedItems(): DeliveryReceiptItem[] {
        if (!this.receipt) {
            return [];
        }
        const deliveryReceiptItems: DeliveryReceiptItem[] = this.receipt.data.items.map(
            (item) => new DeliveryReceiptItem(item, this.receipt!.data.id, this.dialog, this._barcodeScanner),
        );

        // Filter items that are not fully stocked yet
        return deliveryReceiptItems.filter((item) => item.quantityStocked < +item.quantity);
    }

    public get hasUnstockedItems(): boolean {
        return this.unstockedItems.length > 0;
    }

    private loadCompanyCodes(): void {
        this.companyCodeDataService
            .getMyActive()
            .pipe(
                finalize(() => {
                    this.hasMultipleCompanyCodes = this.companyCodes.length > 1;
                    this.updateSelectedCompanyCode();
                }),
            )
            .subscribe({
                next: (codes) => (this.companyCodes = codes),
                error: () => (this.companyCodes = []),
            });
    }

    private updateSelectedCompanyCode(): void {
        const companyCodeId = this.receipt?.data?.companyCodeId;
        if (!companyCodeId || !this.companyCodes.length) {
            this.selectedCompanyCode = undefined;
            return;
        }

        this.selectedCompanyCode = this.companyCodes.find((code) => code.id === companyCodeId);
    }

    public get displayCompanyCode(): boolean {
        return this.hasMultipleCompanyCodes && !!this.selectedCompanyCode;
    }

    public getData(receiptId: number): void {
        this.deliveryReceiptDataService.getById(receiptId).subscribe({
            next: (receipt) => {
                this.breadcrumbService.set(
                    BC.MY_ADMIN.LIST,
                    BC.MY_ADMIN.DELIVERY.LIST,
                    BC.MY_ADMIN.DELIVERY.DETAIL(receipt.data.id, receipt.data.number),
                );
                this.receiptsGroupedByOrder = this.deliveryReceiptDetailService.groupItemsByOrder(receipt.data.items);
                this.receipt = receipt;
                this.loadLinkedDocuments(receipt.data.id);
                this.updateSelectedCompanyCode();
                this.commentCtrl.setValue(receipt.data.comment, { emitEvent: false });
                // Disable comment field after receipt is created (read-only)
                this.commentCtrl.disable({ emitEvent: false });
                if (this.route.snapshot.queryParams['postCreation']) {
                    this.stockEverything();
                }
            },
            error: (error: HttpErrorResponse) => {
                this.errorStatusCode = error.status;
            },
        });
    }

    public upload(): void {
        this.deliveryReceiptFileService.upload(this.receipt!.data);
    }

    public deleteFile(receiptId: number, fileId: string): void {
        this.deliveryReceiptFileService.deleteFile(receiptId, fileId, this.receipt!.data);
    }

    async ngOnInit(): Promise<void> {
        this.loadCompanyCodes();
        if (!this.isDialog) {
            this.route.params.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
                this.userService.getViewFromUserType().then((view: View) => (this.view = view));
                this.getData(this.receiptId);
            });
        } else {
            this.getData(this.receiptId);
        }
    }

    public closeDialog() {
        this._dialogRef.close();
    }

    loadLinkedDocuments(receiptId: number): void {
        this._processChainDataService
            .getRelatedDocuments(ProcessChainDocumentType.DELIVERY_RECEIPT, String(receiptId))
            .subscribe({
                next: (relatedDocuments) => this.fetchedLinkedDocuments.set(relatedDocuments),
                error: (error) => console.error('Failed to load related documents', error),
            });
    }

    navigateToLinkedDocument(linkedDocument: ProcessChainRelatedDocument & { targetRoute: Array<string | number> | null }): void {
        if (!linkedDocument.targetRoute) {
            return;
        }
        const extras = this.buildLinkedDocumentNavigationExtras(linkedDocument);
        if (extras) {
            void this._router.navigate(linkedDocument.targetRoute, extras);
        } else {
            void this._router.navigate(linkedDocument.targetRoute);
        }
    }

    private resolveLinkedDocumentRoute(linkedDocument: ProcessChainRelatedDocument): Array<string | number> | null {
        switch (linkedDocument.documentType) {
            case ProcessChainDocumentType.ORDER:
                return [View.CLIENT, 'order', linkedDocument.documentId, 'detail'];
            case ProcessChainDocumentType.DELIVERY_RECEIPT:
                return [View.CLIENT, 'order', 'receipt', linkedDocument.documentId];
            case ProcessChainDocumentType.ORDER_CONFIRMATION:
                return [View.CLIENT, 'order', 'confirmation', linkedDocument.documentId];
            case ProcessChainDocumentType.INVOICE:
                return [View.CLIENT, 'my-admin', 'invoice', linkedDocument.documentId, 'detail'];
            case ProcessChainDocumentType.PURCHASE_REQUISITION:
                return [View.CLIENT, 'rfq', 'purchase-requisition', linkedDocument.documentId, 'detail'];
            case ProcessChainDocumentType.RFQ:
                return [View.CLIENT, 'rfq', linkedDocument.documentId, 'detail'];
            case ProcessChainDocumentType.QUOTATION:
                return [View.CLIENT, 'rfq', linkedDocument.documentId, 'quotation'];
            default:
                return null;
        }
    }

    private buildLinkedDocumentNavigationExtras(linkedDocument: ProcessChainRelatedDocument): NavigationExtras | undefined {
        if (
            linkedDocument.documentType === ProcessChainDocumentType.DELIVERY_RECEIPT &&
            linkedDocument.documentNumber
        ) {
            return { queryParams: { receiptNumber: linkedDocument.documentNumber } };
        }
        return undefined;
    }

    async stockEverything(): Promise<void> {
        const warehouseSettings = await firstValueFrom(this.warehouseDataService.getSettings());
        if (
            !this.hasUnstockedItems ||
            !warehouseSettings.active ||
            !this.receipt?.possibleActions.includes(DeliveryReceiptPossibleActionType.WAREHOUSE_ADD)
        ) {
            // All items are already fully stocked
            return;
        }

        // Open bulk storage dialog
        const dialogData = new BulkInsertIntoWarehouseDialogData(this.unstockedItems);
        const dialogRef = this.dialog.open(BulkInsertIntoWarehouseDialogComponent, DialogOptions.MEDIUM(dialogData));
        // Hold on to the dialog instance: MatDialog resets componentInstance to null right after
        // closing, so reading it later (e.g. from an asynchronous subscriber) is not reliable.
        const dialogInstance = dialogRef.componentInstance;

        dialogRef
            .afterClosed()
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe((result: BulkInsertIntoWarehouseDialogResult | undefined) => {
                this._router.navigate([View.CLIENT, 'order', 'receipt', this.receiptId], {
                    queryParams: { receiptNumber: this.receipt!.data.number },
                });
                // Escape or backdrop click dismiss the dialog without a result, even when items
                // were already stocked - the dialog itself still knows about that.
                if (result?.stockChanged ?? dialogInstance.stockChanged) {
                    // Refresh the receipt data to show updated stock quantities
                    this.getData(this.receiptId);
                }
            });
    }
}
